import { ref, watch } from 'vue'
import { useOrgStore } from '@/stores/org'
import { useAppStore } from '@/stores/app'

// A short-lived credential bound to the selected org: minted on login and org
// switch, refreshed ~5 min before expiry, dropped on logout. `mint(orgId)`
// calls apiv3 and resolves to { expiresAt } (seconds); the credential itself
// is usually an HttpOnly cookie the browser attaches on its own.
//
// On org switch the snapshot is dropped at once, so callers stop emitting the
// previous org's URLs. A failed refresh keeps a credential that hasn't
// expired yet and retries in a minute, so one transient error doesn't cut
// off working tiles or thumbnails.
const RETRY_MS = 60_000

export function createOrgBoundSession(name, mint) {
  const session = ref(null) // { orgId, expiresAt }
  let refreshTimer = null
  let activeOrgId = null

  function clearTimer() {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  function scheduleRefresh(ms) {
    clearTimer()
    refreshTimer = setTimeout(() => {
      refresh(activeOrgId).catch(e => console.error(`${name} refresh failed`, e))
    }, ms)
  }

  function stillValid(current) {
    return current && current.orgId === activeOrgId && current.expiresAt * 1000 > Date.now()
  }

  async function refresh(orgId) {
    if (!orgId) return null
    if (activeOrgId !== null && activeOrgId !== orgId) {
      session.value = null
      clearTimer()
    }
    activeOrgId = orgId

    try {
      const { expiresAt } = await mint(orgId)
      // Discard late responses: the active org changed mid-request.
      if (activeOrgId !== orgId) return null
      session.value = { orgId, expiresAt }
      scheduleRefresh(Math.max((expiresAt * 1000) - Date.now() - 5 * 60 * 1000, RETRY_MS))
      return session.value
    } catch (err) {
      console.error(`${name} refresh failed`, err)
      if (activeOrgId !== orgId) return null
      if (!stillValid(session.value)) session.value = null
      scheduleRefresh(RETRY_MS)
      return session.value
    }
  }

  function clear() {
    session.value = null
    activeOrgId = null
    clearTimer()
  }

  // Bind the lifecycle to the selected org. The selected org is restored from
  // localStorage before auth completes, so gate on the session too, or this
  // fires an authenticated request at boot and never re-fires. Returns the
  // watch stop handle for HMR / teardown.
  function bindToOrg() {
    const activeOrg = () => useAppStore().isLoggedIn ? useOrgStore().selectedOrg : null

    const initial = activeOrg()
    if (initial) refresh(String(initial))

    return watch(activeOrg, (newOrgId) => {
      if (newOrgId) refresh(String(newOrgId))
      else clear()
    })
  }

  return { session, refresh, clear, bindToOrg }
}
